import { Col, Row } from 'antd';
import { Pin } from 'types';
import { formatToDate } from 'utils/formatToDate';

import { DocumentDetailsModel } from 'api/documents/documents.model';
import { BasicCard } from 'components/BasicCard';
import CardRow from 'components/CardRow';
import CompositionRow from 'components/CompositionRow';
import Detail from 'components/Detail';
import Map from 'components/Map';
import { extractCoordinatesFromProduct } from 'components/Map/utils';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';
import useCertificateHelper from 'hooks/useCertificateHelper';
import {
  ADDITIONAL_DETAILS,
  CertificateDetailsModel,
  NESTED_DETAILS,
  PRODUCER_DETAILS,
} from 'models/certificate.model';
import { COMPANY } from 'models/company.model';

import RelatedCertificates from '../components/RelatedCertificates';
import RelatedDocuments from '../components/RelatedDocuments';
import ScoreCard from '../components/ScoreCard';
import ScoreDetail from '../components/ScoreDetail';
import Template, { ScoreContainer } from '../components/Template';

const BatteryCells = ({
  additionalDetails = {} as CertificateDetailsModel,
  ...document
}: DocumentDetailsModel<CertificateDetailsModel>) => {
  const { getLabel, getValueByLabel } = useCertificateHelper();

  const pins: Pin[] = [];

  if (additionalDetails[ADDITIONAL_DETAILS.PRODUCER]) {
    const coordinates = extractCoordinatesFromProduct(
      additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
    );

    if (coordinates) {
      pins.push({
        coordinates,
        iconPath: '/pin-blue.svg',
      });
    }
  }

  return (
    <Template
      type={document.category}
      jobId={document.jobId}
      favourite={document.favourite}
      batchCard={
        <>
          <Detail label={getLabel('batteryCellsBatchNo')} value={additionalDetails.productId} />
          <Detail label={getLabel('passportId')} value={document.documentBlockchainAddress} />
          <CardRow>
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.ASSEMBLY_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.ASSEMBLY_NUMBER]}
            />
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.CONTROL_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.CONTROL_NUMBER]}
            />
          </CardRow>
        </>
      }
      detailsCard={
        <CardRow>
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_CELL_MODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.CHEMISTRY)}
            value={getValueByLabel(
              NESTED_DETAILS.CHEMISTRY,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.TECHNICAL_SPEC)}
            value={getValueByLabel(
              NESTED_DETAILS.TECHNICAL_SPEC,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.CERTIFICATES_OF_CONFORMITY)}
            value={getValueByLabel(
              NESTED_DETAILS.CERTIFICATES_OF_CONFORMITY,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.TARIC_CODE)}
            value={getValueByLabel(
              NESTED_DETAILS.TARIC_CODE,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_GLOBAL_TRADE_IDENTIFICATION_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_CELL_GLOBAL_TRADE_IDENTIFICATION_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_PRODUCTION_DATE)}
            value={formatToDate(
              getValueByLabel(
                NESTED_DETAILS.BATTERY_CELL_PRODUCTION_DATE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )
            )}
          />
          <Detail
            label={getLabel(PRODUCER_DETAILS.COMPANY_FULL_NAME)}
            value={getValueByLabel(
              PRODUCER_DETAILS.COMPANY_FULL_NAME,
              additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
            )}
          />
        </CardRow>
      }
    >
      <RelatedDocuments
        history={document.history}
        category={document.category}
        company={COMPANY.BATTERY_CELLS}
        options={[
          { value: 'Certificates of Conformity 1', label: getLabel('certificatesOfConformity1') },
          { value: 'Certificate of Conformity 2', label: getLabel('certificatesOfConformity2') },
        ]}
      />
      <RelatedCertificates
        additionalDetails={additionalDetails}
        relatedFields={['chemistryBatchNumber']}
      />

      <SectionTitle>{getLabel('scoring')}</SectionTitle>
      <BasicCard $justify="center">
        <ScoreContainer>
          <ScoreCard label={getLabel('productScore')}>
            <Detail label={getLabel('batteryCellsBatchNo')} value={additionalDetails.productId} />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_CELL_CHILD_LABOR_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_CELL_CHILD_LABOR_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.BATTERY_CELLS_HUMAN_RIGHTS_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.BATTERY_CELLS_HUMAN_RIGHTS_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
          </ScoreCard>
          <ScoreCard label={getLabel('batteryCellProducerScore')}>
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.CHILD_LABOR_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <ScoreDetail
              label={getLabel(PRODUCER_DETAILS.ESG_SCORING)}
              maxScore={6}
              score={getValueByLabel(
                PRODUCER_DETAILS.ESG_SCORING,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
          </ScoreCard>
          <ScoreCard label={getLabel('chemistryScore')}>
            <Detail
              label={getLabel(NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER)}
              value={getValueByLabel(
                NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.MATERIALS_CHILD_LABOR_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.MATERIALS_CHILD_LABOR_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
            <ScoreDetail
              label={getLabel(NESTED_DETAILS.MATERIALS_HUMAN_RIGHTS_AVERAGE)}
              maxScore={6}
              score={getValueByLabel(
                NESTED_DETAILS.MATERIALS_HUMAN_RIGHTS_AVERAGE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
          </ScoreCard>
        </ScoreContainer>
      </BasicCard>

      <SectionTitle>{getLabel('specification')}</SectionTitle>
      <Row gutter={[16, 16]}>
        <Col md={24} lg={12}>
          <SubTitle>{getLabel('generalInfo')}</SubTitle>
          <CompositionRow<NESTED_DETAILS>
            composition={additionalDetails[ADDITIONAL_DETAILS.DETAILS]}
            labels={[
              NESTED_DETAILS.WEIGHT,
              NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY_CLEAN,
              NESTED_DETAILS.INTERNAL_RESISTANCE,
              NESTED_DETAILS.NOMINAL_CAPACITY,
              NESTED_DETAILS.AVERAGE_NOMINAL_CAPACITY,
              NESTED_DETAILS.VOLTAGE,
              NESTED_DETAILS.NOMINAL_VOLTAGE,
              NESTED_DETAILS.ENERGY,
              NESTED_DETAILS.FORMAT,
            ]}
          />
        </Col>
        <Col md={24} lg={12}>
          <SubTitle>{getLabel('operationalInfo')}</SubTitle>
          <BasicCard>
            <CardRow>
              <Detail
                label={getLabel(NESTED_DETAILS.CHARGING_METHOD)}
                value={getValueByLabel(
                  NESTED_DETAILS.CHARGING_METHOD,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )}
              />
              <Detail
                label={getLabel(NESTED_DETAILS.CHARGING_CURRENT)}
                value={getValueByLabel(
                  NESTED_DETAILS.CHARGING_CURRENT,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )}
              />
              <Detail
                label={getLabel(NESTED_DETAILS.CHARGING_TIME)}
                value={getValueByLabel(
                  NESTED_DETAILS.CHARGING_TIME,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )}
              />
              <Detail
                label={getLabel(NESTED_DETAILS.CHARGING_CYCLES)}
                value={getValueByLabel(
                  NESTED_DETAILS.CHARGING_CYCLES,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )}
              />
              <Detail
                label={getLabel(NESTED_DETAILS.DISCHARGE_POWER)}
                value={getValueByLabel(
                  NESTED_DETAILS.DISCHARGE_POWER,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )}
              />
            </CardRow>
          </BasicCard>
        </Col>
      </Row>

      <SectionTitle>{getLabel('batteryCellProducer')}</SectionTitle>
      <Map
        pins={pins}
        card={
          <BasicCard>
            <SectionTitle noMargin dividerMargin="1rem">
              {getLabel(ADDITIONAL_DETAILS.PRODUCER)}
            </SectionTitle>
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY_FULL_NAME)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY_FULL_NAME,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY_HEADQUATERS)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY_HEADQUATERS,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.FACTORY_LOCATION)}
              value={getValueByLabel(
                PRODUCER_DETAILS.FACTORY_LOCATION,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.COORDINATES)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COORDINATES,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(NESTED_DETAILS.TARIC_CODE)}
              value={getValueByLabel(
                NESTED_DETAILS.TARIC_CODE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
          </BasicCard>
        }
      />
    </Template>
  );
};

export default BatteryCells;
